import { Component, EventEmitter, forwardRef, Input, HostListener, ElementRef, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Language, UpdateTranslationOverride, UpdateTranslationOverrideLanguage } from '../../api';

export type InputTranslationsSaveState = 'idle' | 'saving' | 'saved' | 'error';

@Component({
  selector: 'app-input-translations',
  standalone: false,
  templateUrl: './input-translations.component.html',
  styleUrls: ['./input-translations.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTranslationsComponent),
      multi: true
    }
  ]
})
export class InputTranslationsComponent implements ControlValueAccessor, OnInit {
  private static readonly TOOLTIP_SEEN_STORAGE_KEY = 'input-translations-tooltip-seen-token';

  @Input() maxLength: number | null = null;
  @Input() lines: number = 1;
  @Input() isQuillEditor: boolean = false;
  @Input() prefix!: string;
  @Input() productId: number | null = null;
  @Input() required: boolean = false;
  @Input({ required: true }) translations!: Record<string, Record<string, string>>;
  @Input({ required: true }) availableLanguages!: Language[];

  public quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, 'link'],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['clean'],
    ]
  };

  @Output() formTouched: EventEmitter<boolean> = new EventEmitter<boolean>();

  selectedLanguage = 'nl';
  dropdownOpen = false;
  value: UpdateTranslationOverrideLanguage[] = [];
  disabled = false;
  showTabTooltip = false;
  saveState: InputTranslationsSaveState = 'idle';
  languages: Language[] = [];

  private savedValueByLanguage: Record<number, string | null> = {};
  public valuesOfInputs: UpdateTranslationOverride = { Languages: [] };

  private onChange = (value: UpdateTranslationOverrideLanguage) => {};
  onTouched = () => { this.formTouched.emit(true); };

  constructor(
    private elRef: ElementRef,
    private translateService: TranslateService
  ) {}

  get isTextarea(): boolean {
    return this.lines > 1;
  }

  get currentCharCount(): number {
    return this.findCurrentLanguageValue(this.selectedLanguage)?.Override?.length ?? 0;
  }

  /** Returns the value from the previous language in the list to use as placeholder. */
  get fallbackPlaceholder(): string {
    const currentIndex = this.languages.findIndex(
      lang => lang.Code === this.selectedLanguage
    );

    if (currentIndex <= 0) {
      return '';
    }

    const prevLanguageCode = this.languages[currentIndex - 1].Code;
    if (prevLanguageCode == undefined) {
      return '';
    }
    return this.findCurrentLanguageValue(prevLanguageCode)?.Override?.trim() || '';
  }

  get currentLanguage(): Language {
    return this.languages.find(lang => lang.Code === this.selectedLanguage)
      ?? this.languages[0];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  ngOnInit(): void {
    this.initialize(this.availableLanguages);
  }

  private initialize(languages: Language[]): void {
    this.languages = languages;
    this.initializeSelectedLanguage();
    this.showTabTooltip = !this.getTabTooltipSeen();
    this.valuesOfInputs.Code = this.productId != null ? this.prefix + this.productId : this.prefix;

    for (const language of this.languages) {
      const override = this.translations[language.Code ?? '']?.[this.valuesOfInputs.Code!] ?? '';
      this.addLanguageValue(language, override);
    }

    this.value = this.valuesOfInputs.Languages!;

    if (this.showTabTooltip) {
      this.setTabTooltipSeen();
    }
  }

  private addLanguageValue(language: Language, override: string): void {
    this.valuesOfInputs.Languages?.push({
      LanguageId: language.Id,
      Default: null,
      Override: override
    });
    this.savedValueByLanguage[language.Id!] = override;
  }

  writeValue(value: UpdateTranslationOverrideLanguage): void {
    if (value) {
      this.value.push(value);
      if (typeof value.LanguageId === 'number') {
        this.savedValueByLanguage[value.LanguageId] = value.Override ?? null;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  findCurrentLanguageValue(languageCode: string) {
    return this.value.find(lang => lang.LanguageId === this.languages.find(l => l.Code?.toLowerCase() === languageCode.toLowerCase())?.Id);
  }

  updateValue(value: string): void {
    const lang = this.findCurrentLanguageValue(this.selectedLanguage);
    if (lang) {
      lang.Override = value;
    }
    this.onChange(lang!);
    this.onTouched();
  }

  /** Called by the template on `blur` of the input/textarea. Persists the current language's value if it changed since the last successful save (or the initial snapshot from `writeValue`). */
  onFieldBlur(): void {
    this.onTouched();
    this.saveCurrentLanguageIfChanged();
  }

  saveCurrentLanguageIfChanged(): void {
    const code = this.valuesOfInputs.Code;
    if (!code || this.disabled) {
      return;
    }

    const languageCode = this.selectedLanguage;
    const languageId = this.languages.find(language => language.Code === languageCode)?.Id;
    if (typeof languageId !== 'number') {
      return;
    }

    const currentRaw = this.findCurrentLanguageValue(languageCode)?.Override ?? '';
    const savedRaw = this.savedValueByLanguage[languageId] ?? '';
    if (currentRaw === savedRaw) {
      return;
    }

    const overrideValue = currentRaw.trim() === '' ? null : currentRaw;
    const languages = this.valuesOfInputs.Languages ?? (this.valuesOfInputs.Languages = []);
    const existing = languages.find(lang => lang.LanguageId === languageId);
    if (existing) {
      existing.Override = overrideValue;
    } else {
      languages.push({ LanguageId: languageId, Default: null, Override: overrideValue });
    }

    this.savedValueByLanguage[languageId] = overrideValue;
    this.setSaveState('saved');
  }

  private setSaveState(state: InputTranslationsSaveState): void {
    this.saveState = state;
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || this.disabled || this.languages.length === 0) {
      return;
    }

    this.dismissTabTooltip();

    const currentIndex = this.languages.findIndex(
      lang => lang.Code === this.selectedLanguage
    );

    if (currentIndex === -1) {
      return;
    }

    const direction = event.shiftKey ? -1 : 1;

    // For Shift+Tab, always navigate to previous language regardless of fill status.
    if (direction === -1) {
      const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : this.languages.length - 1;
      event.preventDefault();
      this.saveCurrentLanguageIfChanged();
      this.selectedLanguage = this.languages[prevIndex].Code ?? 'nl';
      return;
    }

    // For Tab (forward), only navigate to unfilled languages if any exist.
    const unfilledIndices = this.languages
      .map((lang, index) => lang.Code && this.hasValue(lang.Code) ? -1 : index)
      .filter(index => index !== -1);

    // Once all languages are filled, restore normal tab navigation.
    if (unfilledIndices.length === 0) {
      return;
    }

    const nextIndex = unfilledIndices.find(index => index > currentIndex) ?? unfilledIndices[0];

    event.preventDefault();
    this.saveCurrentLanguageIfChanged();
    this.selectedLanguage = this.languages[nextIndex].Code ?? 'nl';
  }

  dismissTabTooltip(): void {
    this.showTabTooltip = false;
    this.setTabTooltipSeen();
  }

  private getTabTooltipSeen(): boolean {
    try {
      return localStorage.getItem(InputTranslationsComponent.TOOLTIP_SEEN_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private setTabTooltipSeen(): void {
    try {
      localStorage.setItem(InputTranslationsComponent.TOOLTIP_SEEN_STORAGE_KEY, '1');
    } catch {
      // Ignore storage errors so the component remains functional.
    }
  }

  private initializeSelectedLanguage(): void {
    const preferredLanguage = this.resolvePreferredLanguageCode();

    if (preferredLanguage) {
      this.selectedLanguage = preferredLanguage;
    } else if (this.languages.length > 0) {
      this.selectedLanguage = this.languages[0].Code ?? 'nl';
    }
  }

  private resolvePreferredLanguageCode(): string | null {
    const candidates = [
      this.translateService.currentLang,
      this.translateService.getDefaultLang()
    ].filter(Boolean) as string[];

    for (const candidate of candidates) {
      const exactMatch = this.languages.find(lang => lang.Code === candidate);
      if (exactMatch) {
        return exactMatch.Code ?? 'nl';
      }

      const baseCode = candidate.split('-')[0];
      const baseMatch = this.languages.find(lang => lang.Code === baseCode);
      if (baseMatch) {
        return baseMatch.Code ?? 'nl';
      }
    }

    return null;
  }

  selectLanguage(code: string): void {
    if (code === this.selectedLanguage) {
      this.dropdownOpen = false;
      return;
    }
    this.saveCurrentLanguageIfChanged();
    this.selectedLanguage = code;
    this.dropdownOpen = false;
  }

  hasValue(code: string): boolean {
    return !!this.findCurrentLanguageValue(code)?.Override?.trim();
  }

  // False while languages are still loading so callers can't treat an empty snapshot as valid.
  get isValid(): boolean {
    if (!this.required) {
      return true;
    }
    if (this.languages.length === 0) {
      return false;
    }
    return this.languages.every(language => this.hasValue(language.Code!));
  }
}